import React, { useState, useEffect, useCallback } from 'react';

/**
 * Connections Game Component
 * Sort 16 cards into 4 groups to reveal facilitator observations
 * Self-contained with inline styles, no external dependencies
 */

const ConnectionsGame = ({
  title = "Discover Connections",
  instructions = "Select 4 cards that share something in common",
  data = {
    groups: []
  },
  onComplete
}) => {
  // Game state
  const [selectedCards, setSelectedCards] = useState([]);
  const [foundGroups, setFoundGroups] = useState([]);
  const [attemptsRemaining, setAttemptsRemaining] = useState(4);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isShaking, setIsShaking] = useState(false);
  const [availableCards, setAvailableCards] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const [hasSkipped, setHasSkipped] = useState(false);

  // Initialize cards from groups
  useEffect(() => {
    if (data.groups && data.groups.length > 0) {
      const allCards = [];
      data.groups.forEach(group => {
        group.cards.forEach(card => {
          allCards.push({
            text: card,
            groupId: group.id || group.name,
            groupName: group.name,
            color: group.color,
            difficulty: group.difficulty
          });
        });
      });
      // Shuffle cards
      const shuffled = [...allCards].sort(() => Math.random() - 0.5);
      setAvailableCards(shuffled);
    }
  }, [data.groups]);

  // Check if game is complete
  useEffect(() => {
    if (foundGroups.length === 4 && !isComplete) {
      setIsComplete(true);
      if (onComplete) {
        setTimeout(onComplete, 1500);
      }
    }
  }, [foundGroups, isComplete, onComplete]);

  // Handle card selection
  const handleCardClick = useCallback((card) => {
    if (foundGroups.find(g => g.id === card.groupId)) {
      return; // Card already in a found group
    }

    setSelectedCards(prev => {
      const isSelected = prev.find(c => c.text === card.text);
      if (isSelected) {
        return prev.filter(c => c.text !== card.text);
      }
      if (prev.length < 4) {
        return [...prev, card];
      }
      return prev;
    });
  }, [foundGroups]);

  // Check if selection is correct
  const checkSelection = useCallback(() => {
    if (selectedCards.length !== 4) return;

    // Check if all selected cards are from the same group
    const groupId = selectedCards[0].groupId;
    const isCorrect = selectedCards.every(card => card.groupId === groupId);

    if (isCorrect) {
      // Correct! Add to found groups
      const group = data.groups.find(g => (g.id || g.name) === groupId);
      setFoundGroups(prev => [...prev, {
        ...group,
        id: groupId,
        cards: selectedCards
      }]);
      setSelectedCards([]);

      // Remove cards from available
      setAvailableCards(prev =>
        prev.filter(card => card.groupId !== groupId)
      );
    } else {
      // Wrong - show feedback
      setIsShaking(true);

      // Check if one away
      const groupCounts = {};
      selectedCards.forEach(card => {
        groupCounts[card.groupId] = (groupCounts[card.groupId] || 0) + 1;
      });

      const isOneAway = Object.values(groupCounts).some(count => count === 3);

      if (isOneAway) {
        setErrorMessage("One away! You're so close.");
      } else {
        setErrorMessage("Not quite. Try again!");
      }

      setShowError(true);
      setAttemptsRemaining(prev => Math.max(0, prev - 1));

      setTimeout(() => {
        setIsShaking(false);
        setShowError(false);
      }, 1500);
    }
  }, [selectedCards, data.groups]);

  // Deselect all cards
  const deselectAll = () => {
    setSelectedCards([]);
  };

  // Shuffle remaining cards
  const shuffleCards = () => {
    setAvailableCards(prev => [...prev].sort(() => Math.random() - 0.5));
  };

  // Skip to insights - reveal all groups
  const handleSkip = () => {
    // Format groups with card objects for consistent display
    const formattedGroups = data.groups.map(group => ({
      ...group,
      id: group.id || group.name,
      cards: group.cards.map(cardText => ({
        text: cardText,
        groupId: group.id || group.name
      }))
    }));
    setFoundGroups(formattedGroups);
    setAvailableCards([]);
    setIsComplete(true);
    setHasSkipped(true);
    if (onComplete) {
      setTimeout(onComplete, 1500);
    }
  };

  // Styles
  const styles = {
    container: {
      fontFamily: 'var(--sans, "DM Sans", sans-serif)',
      maxWidth: '800px',
      margin: '0 auto',
      padding: '20px',
      position: 'relative',
    },
    header: {
      textAlign: 'center',
      marginBottom: '30px',
    },
    title: {
      fontFamily: 'var(--serif, "DM Serif Display", serif)',
      fontSize: 'var(--text-2xl, 28px)',
      fontWeight: '600',
      color: 'var(--navy, #1B2A4A)',
      marginBottom: '10px',
    },
    instructions: {
      fontFamily: 'var(--sans, "DM Sans", sans-serif)',
      fontSize: 'var(--text-base, 15px)',
      color: 'var(--charcoal, #2D2D2D)',
      marginBottom: '20px',
    },
    attemptsBar: {
      display: 'flex',
      justifyContent: 'center',
      gap: '8px',
      marginBottom: '20px',
    },
    attemptDot: {
      width: '12px',
      height: '12px',
      borderRadius: '50%',
      backgroundColor: 'var(--light, #F2EDE6)',
      transition: 'background-color 0.3s ease',
    },
    attemptDotFilled: {
      backgroundColor: 'var(--charcoal, #2D2D2D)',
    },
    foundGroupsContainer: {
      marginBottom: '20px',
    },
    foundGroup: {
      marginBottom: '15px',
      padding: '20px',
      borderRadius: '12px',
      animation: 'slideDown 0.5s ease-out',
    },
    foundGroupHeader: {
      fontSize: '18px',
      fontWeight: '600',
      marginBottom: '8px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    foundGroupCards: {
      fontSize: '14px',
      color: 'rgba(0, 0, 0, 0.8)',
      marginBottom: '12px',
    },
    foundGroupReveal: {
      fontSize: '15px',
      lineHeight: '1.6',
      color: '#333',
      borderTop: '1px solid rgba(0, 0, 0, 0.1)',
      paddingTop: '12px',
      marginTop: '12px',
    },
    cardsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: '10px',
      marginBottom: '20px',
      animation: isShaking ? 'shake 0.5s ease-in-out' : 'none',
    },
    card: {
      padding: '20px 15px',
      borderRadius: 'var(--radius-md, 8px)',
      backgroundColor: 'var(--light, #F2EDE6)',
      border: '2px solid transparent',
      cursor: 'pointer',
      textAlign: 'center',
      fontSize: 'var(--text-base, 15px)',
      fontWeight: '500',
      fontFamily: 'var(--mono, "JetBrains Mono", monospace)',
      transition: 'all var(--duration-fast, 200ms) ease',
      minHeight: '80px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      userSelect: 'none',
    },
    cardSelected: {
      backgroundColor: 'var(--cream, #FFF9F0)',
      borderColor: 'var(--gold, #F7C948)',
      transform: 'scale(0.98)',
    },
    cardHover: {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    },
    controls: {
      display: 'flex',
      justifyContent: 'center',
      gap: '10px',
      marginTop: '20px',
    },
    button: {
      padding: '10px 20px',
      borderRadius: 'var(--radius-md, 8px)',
      border: '2px solid transparent',
      fontSize: 'var(--text-sm, 14px)',
      fontWeight: '500',
      fontFamily: 'var(--sans, "DM Sans", sans-serif)',
      cursor: 'pointer',
      transition: 'all var(--duration-fast, 200ms) ease',
    },
    checkButton: {
      backgroundColor: 'transparent',
      color: 'var(--gold, #F7C948)',
      borderColor: 'var(--gold, #F7C948)',
    },
    secondaryButton: {
      backgroundColor: 'var(--light, #F2EDE6)',
      color: 'var(--charcoal, #2D2D2D)',
      borderColor: 'var(--light, #F2EDE6)',
    },
    skipButton: {
      position: 'absolute',
      top: '10px',
      right: '10px',
      padding: '6px 12px',
      fontSize: 'var(--text-xs, 12px)',
      backgroundColor: 'transparent',
      color: 'var(--mid, #B8AFA4)',
      borderColor: 'var(--mid, #B8AFA4)',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderRadius: 'var(--radius-md, 8px)',
      fontFamily: 'var(--sans, "DM Sans", sans-serif)',
      cursor: 'pointer',
      transition: 'all var(--duration-fast, 200ms) ease',
      zIndex: 10,
    },
    buttonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    errorMessage: {
      textAlign: 'center',
      color: 'var(--rust, #C4652A)',
      fontSize: 'var(--text-sm, 14px)',
      fontFamily: 'var(--sans, "DM Sans", sans-serif)',
      marginTop: '10px',
      minHeight: '20px',
      animation: 'fadeIn 0.3s ease',
    },
    completionMessage: {
      textAlign: 'center',
      padding: '40px',
      fontSize: 'var(--text-2xl, 28px)',
      color: 'var(--teal, #1A8A7D)',
      fontWeight: '600',
      fontFamily: 'var(--serif, "DM Serif Display", serif)',
      animation: 'fadeIn 0.5s ease',
    },
  };

  // Group colors based on difficulty or custom color
  const getGroupColor = (group) => {
    // If group has a custom color, use it with opacity
    if (group.color) {
      // Convert hex to rgba with opacity
      const hex = group.color.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      return `rgba(${r}, ${g}, ${b}, 0.2)`;
    }

    // Otherwise use difficulty-based colors
    const difficultyColors = {
      1: 'rgba(247, 201, 72, 0.2)',     // Gold with opacity
      2: 'rgba(26, 138, 125, 0.2)',      // Teal with opacity
      3: 'rgba(27, 42, 74, 0.2)',        // Navy with opacity
      4: 'rgba(196, 101, 42, 0.2)',      // Rust with opacity
    };
    return difficultyColors[group.difficulty] || 'var(--light, #F2EDE6)';
  };

  // Mobile responsive adjustments
  const isMobile = window.innerWidth < 640;
  if (isMobile) {
    styles.cardsGrid.gridTemplateColumns = 'repeat(2, 1fr)';
    styles.card.fontSize = '14px';
    styles.card.minHeight = '70px';
    styles.card.padding = '15px 10px';
    styles.header.paddingTop = '40px'; // Add padding to avoid button overlap
    styles.skipButton.fontSize = '11px';
    styles.skipButton.padding = '5px 10px';
  }

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }
      `}</style>

      {/* Skip button */}
      {!isComplete && (
        <button
          style={styles.skipButton}
          onClick={handleSkip}
          aria-label="Show results and view all insights"
        >
          Show Results
        </button>
      )}

      <div style={styles.header}>
        <h2 style={styles.title}>{title}</h2>
        <p style={styles.instructions}>{instructions}</p>

        {/* Attempts remaining indicator */}
        <div style={styles.attemptsBar}>
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              style={{
                ...styles.attemptDot,
                ...(i < attemptsRemaining ? styles.attemptDotFilled : {}),
              }}
            />
          ))}
        </div>
      </div>

      {/* Found groups */}
      {foundGroups.length > 0 && (
        <div style={styles.foundGroupsContainer}>
          {foundGroups.map((group, index) => (
            <div
              key={group.id}
              style={{
                ...styles.foundGroup,
                backgroundColor: getGroupColor(group),
              }}
            >
              <div style={styles.foundGroupHeader}>{group.name}</div>
              <div style={styles.foundGroupCards}>
                {group.cards.map(c => c.text).join(', ')}
              </div>
              {(group.revealText || group.insight) && (
                <div style={styles.foundGroupReveal}>
                  {group.revealText || group.insight}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Game complete message */}
      {isComplete ? (
        <div style={styles.completionMessage}>
          {hasSkipped ? '✓ All insights revealed' : '🎉 Excellent! You\'ve discovered all connections!'}
        </div>
      ) : (
        <>
          {/* Cards grid */}
          <div style={styles.cardsGrid}>
            {availableCards.map((card, index) => {
              const isSelected = selectedCards.find(c => c.text === card.text);
              return (
                <div
                  key={`${card.text}-${index}`}
                  style={{
                    ...styles.card,
                    ...(isSelected ? styles.cardSelected : {}),
                  }}
                  onClick={() => handleCardClick(card)}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }
                  }}
                >
                  {card.text}
                </div>
              );
            })}
          </div>

          {/* Controls */}
          <div style={styles.controls}>
            <button
              style={{
                ...styles.button,
                ...styles.secondaryButton,
              }}
              onClick={shuffleCards}
            >
              Shuffle
            </button>
            <button
              style={{
                ...styles.button,
                ...styles.secondaryButton,
                ...(selectedCards.length === 0 ? styles.buttonDisabled : {}),
              }}
              onClick={deselectAll}
              disabled={selectedCards.length === 0}
            >
              Deselect All
            </button>
            <button
              style={{
                ...styles.button,
                ...styles.checkButton,
                ...(selectedCards.length !== 4 ? styles.buttonDisabled : {}),
              }}
              onClick={checkSelection}
              disabled={selectedCards.length !== 4}
            >
              Submit
            </button>
          </div>

          {/* Error message */}
          <div style={styles.errorMessage}>
            {showError && errorMessage}
          </div>
        </>
      )}
    </div>
  );
};

export default ConnectionsGame;